/**
 * Audit Command
 *
 * Display rotation history and audit trail for a credential.
 */

import { CredentialService } from '../core/service';
import * as output from '../utils/output';

interface AuditOptions {
  json?: boolean;
}

/**
 * Format a date for display
 */
function formatDate(date: Date | string | undefined): string {
  if (!date) return 'Unknown';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString();
}

export async function auditCommand(name: string, options: AuditOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await service.init();

  // Get credential metadata
  const credential = await service.get(name);
  if (!credential) {
    output.error(`Credential "${name}" not found in vault.`);
    const list = await service.list();
    if (list.length > 0) {
      output.info('Available credentials:');
      list.forEach((c) => console.log(`  ${c.name}`));
    }
    process.exit(1);
  }

  const metadata = credential.metadata;

  // Get access log
  const accessLog = await service.getAccessLog(name);

  if (options.json) {
    // Machine-readable output
    output.jsonOutput({
      name,
      createdAt: metadata?.createdAt,
      updatedAt: metadata?.updatedAt,
      rotationCount: metadata?.rotationHistory?.length || 0,
      rotationHistory: metadata?.rotationHistory || [],
      accessCount: accessLog.length,
      accessLog,
    });
    return;
  }

  // Human-readable output
  console.log('');
  output.header(name);
  output.divider();
  output.keyValue('Created', formatDate(metadata?.createdAt));
  output.keyValue('Last updated', formatDate(metadata?.updatedAt));

  const rotations = metadata?.rotationHistory || [];
  output.keyValue('Total rotations', String(rotations.length));

  if (rotations.length > 0) {
    console.log('');
    output.header('Rotation History');
    output.divider();

    // Show rotations in reverse chronological order (most recent first)
    const sortedRotations = [...rotations].reverse();
    sortedRotations.forEach((event, index) => {
      const num = rotations.length - index;
      const timestamp = formatDate(event.timestamp);
      const reason = event.reason || 'No reason provided';
      console.log(`  ${output.colors.muted(`#${num}`)} ${timestamp}`);
      console.log(`     ${output.colors.dim('Reason:')} ${reason}`);
    });
  } else {
    console.log('');
    output.info('No rotation history. This credential has not been rotated.');
  }

  // Show access log
  if (accessLog.length > 0) {
    console.log('');
    output.header('Recent Access Log');
    output.divider();
    output.keyValue('Total accesses', String(accessLog.length));
    console.log('');

    // Show last 10 accesses in reverse chronological order
    const recentAccesses = [...accessLog].reverse().slice(0, 10);
    recentAccesses.forEach((entry) => {
      const timestamp = formatDate(entry.timestamp);
      const actionColor = entry.action === 'grant' ? output.colors.warning : output.colors.muted;
      console.log(`  ${timestamp}  ${actionColor(entry.action)}`);
    });

    if (accessLog.length > 10) {
      console.log(`  ${output.colors.dim(`... and ${accessLog.length - 10} more`)}`);
    }
  } else {
    console.log('');
    output.info('No access log. This credential has not been accessed yet.');
  }

  console.log('');
}
